import type { ProductTypeCategory } from '../../prisma/enums';

/**
 * The Clear21 vehicle-assessment feature is deliberately NOT PostHog-flagged
 * (decision, 13 Jul 2026): the sole gate is the claim's ProductType.category —
 * an explicit per-tenant opt-in flipped in Configuration → Product types.
 * See docs/plan/clear21-assessing-integration/plan.md.
 */

/** Product-type categories whose claims carry vehicle-specific features. */
export const VEHICLE_PRODUCT_CATEGORIES = ['FLEET', 'MOTOR'] as const satisfies readonly ProductTypeCategory[];

/**
 * Single category rule shared by the client (tab visibility) and the server
 * (procedure gate): the vehicle features — Clear21 vehicle assessment and Ravin AI
 * vehicle inspection — exist only on claims whose product type is categorised FLEET
 * or MOTOR. Named for the product type rather than for either feature: it is a fact
 * about the product, and both features ask the same question of it.
 *
 * Note `category` defaults to GENERAL, so a product type only becomes a vehicle one
 * when someone sets it in Configuration → Product types. A product type merely *named*
 * "Motor Insurance" does not qualify.
 */
export function isVehicleProductCategory(category: ProductTypeCategory | null | undefined): boolean {
    return category != null && VEHICLE_PRODUCT_CATEGORIES.some((candidate) => candidate === category);
}

/**
 * VehicleAssessmentEvent.eventType written when the assessor's returned figure
 * lands on the Clear21 register. Shared because it has three readers that must
 * not drift: the server-side recorder (writer), the repair-progress reader, and
 * the client's Recent updates rendering.
 */
export const REGISTER_ASSESSED_TOTAL_EVENT = 'register.assessedTotal';

/**
 * The remaining VehicleAssessmentEvent.eventType values the register differ
 * writes. Shared for the same reason as REGISTER_ASSESSED_TOTAL_EVENT: the
 * client buckets the claim's repair-journey activity per stage by eventType,
 * and a drifted literal would silently drop events from the journey.
 */
export const REGISTER_STATUS_EVENT = 'register.assessmentStatus';
export const REGISTER_REPAIR_STAGE_EVENT = 'register.repairStage';
export const REGISTER_REPAIR_BOOKING_EVENT = 'register.repairBooking';

/**
 * Clear21 is a .NET system and uses DateTime.MinValue (`0001-01-01`) for an
 * unset date. JavaScript accepts that as a real Date, so every consumer must
 * apply the same floor before treating a provider timestamp as evidence.
 */
export const EARLIEST_PLAUSIBLE_CLEAR21_DATE_MS = Date.UTC(1900, 0, 1);

export function parseClear21Date(value: string | undefined): Date | null {
    if (!value) {
        return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()) || parsed.getTime() < EARLIEST_PLAUSIBLE_CLEAR21_DATE_MS) {
        return null;
    }
    return parsed;
}

export const REPAIR_JOURNEY_STAGE_KEYS = ['AI_DAMAGE_SCAN', 'REPAIR_QUOTE', 'QUOTE_ASSESSMENT', 'REPAIR'] as const;

export type RepairJourneyStageKey = (typeof REPAIR_JOURNEY_STAGE_KEYS)[number];

const KNOWN_CLEAR21_EVENT_STAGES = {
    AssessmentCreated: 'REPAIR_QUOTE',
    RepairerAssigned: 'REPAIR_QUOTE',
    EstimateRequestedFromRepairer: 'REPAIR_QUOTE',
    AssessmentNotifiedOfEstimateReceived: 'REPAIR_QUOTE',
    RepairAssessedAmountSet: 'QUOTE_ASSESSMENT',
    ClaimApproved: 'QUOTE_ASSESSMENT',
    AuthoritySentToRepairer: 'REPAIR',
    RepairStagesUpdated: 'REPAIR',
} as const satisfies Record<string, RepairJourneyStageKey>;

const REGISTER_EVENT_STAGES = {
    [REGISTER_STATUS_EVENT]: 'QUOTE_ASSESSMENT',
    [REGISTER_ASSESSED_TOTAL_EVENT]: 'QUOTE_ASSESSMENT',
    [REGISTER_REPAIR_STAGE_EVENT]: 'REPAIR',
    [REGISTER_REPAIR_BOOKING_EVENT]: 'REPAIR',
} as const satisfies Record<string, RepairJourneyStageKey>;

export function clear21EventName(eventType: string): string {
    return eventType.slice(eventType.lastIndexOf('/') + 1);
}

/**
 * Files every observed assessment event into the journey stage it narrates.
 * This is shared by the server's fair history allocation and the client's
 * rendering so a vocabulary change cannot silently starve or hide a stage.
 */
export function repairJourneyStageForEvent(eventType: string): RepairJourneyStageKey {
    const eventName = clear21EventName(eventType);
    const knownStage = KNOWN_CLEAR21_EVENT_STAGES[eventName as keyof typeof KNOWN_CLEAR21_EVENT_STAGES];
    if (knownStage) {
        return knownStage;
    }
    const registerStage = REGISTER_EVENT_STAGES[eventType as keyof typeof REGISTER_EVENT_STAGES];
    if (registerStage) {
        return registerStage;
    }

    const key = eventName.toLowerCase();
    if (
        (key.startsWith('repair') && !key.startsWith('repairer')) ||
        key.includes('authority') ||
        key.includes('vehicle') ||
        key.includes('job') ||
        key.includes('collect')
    ) {
        return 'REPAIR';
    }
    if (key.includes('assess') || key.includes('approv') || key.includes('authoris')) {
        return 'QUOTE_ASSESSMENT';
    }
    if (key.includes('estimate') || key.includes('quote') || key.includes('repairer')) {
        return 'REPAIR_QUOTE';
    }
    return 'QUOTE_ASSESSMENT';
}

export const VEHICLE_ASSESSMENT_STATUS_LABELS = {
    REQUEST_SENT: 'Request sent',
    REQUEST_FAILED: 'Request failed',
    CREATED: 'In progress',
    CLOSED: 'Closed',
} as const;

export const PRODUCT_TYPE_CATEGORY_LABELS = {
    GENERAL: 'General',
    FLEET: 'Fleet',
    MOTOR: 'Motor',
} as const satisfies Record<ProductTypeCategory, string>;
